import React from 'react';
import { Dialog, DialogContent, useTheme } from '@mui/material';
import { ThemeMode } from 'config';

interface DialogProps {
    open: boolean;
    title: string;
    description: string;
    isLoading: boolean;
    loadingButtonTitle: string;
    Graphic: React.ReactNode;
    handleClose: () => void;
    handleConfirm: () => void;
    hasfields?: boolean;
    children?: React.ReactNode; // ✅ Accepts custom input (e.g., TextField)
}

const AppDialog: React.FC<DialogProps> = ({
    open,
    title,
    description,
    isLoading,
    loadingButtonTitle,
    Graphic,
    handleClose,
    handleConfirm,
    hasfields,
    children // ✅ Rendered only when needed
}) => {
    const { palette } = useTheme();
    const backgroundColor = palette.mode === ThemeMode.DARK ? '[#212946]' : 'white';
    const titleColor = palette.mode === ThemeMode.DARK ? '#7078A8' : '#101828';
    const descriptionColor = palette.mode === ThemeMode.DARK ? '#939aa9' : '#667085';
    const cancelButtonValue = palette.mode === ThemeMode.DARK ? '200' : '500';
    const cancelButtonHoverValue = palette.mode === ThemeMode.DARK ? '400' : '700';

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogContent>
                <div className={`flex flex-col justify-start items-center w-full relative overflow-hidden gap-8 bg-${backgroundColor}`}>
                    <div className="flex flex-col justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-5">
                        {Graphic}
                        <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-2">
                            <p
                                className={`self-stretch flex-grow-0 flex-shrink-0 w-full text-lg font-bold text-center text-[${titleColor}]`}
                            >
                                {title}
                            </p>
                            <p className={`self-stretch flex-grow-0 flex-shrink-0 w-full text-sm text-center text-[${descriptionColor}]`}>
                                {description}
                            </p>
                        </div>
                    </div>

                    {/* ✅ Allow parent to pass custom inputs (like TextField) */}
                    {hasfields && children}

                    <div className="flex justify-evenly items-center self-stretch flex-grow-0 flex-shrink-0 gap-3">
                        <button
                            onClick={handleClose}
                            className={`mr-2 border border-gray-${cancelButtonValue} text-gray-${cancelButtonValue} hover:text-gray-${cancelButtonHoverValue} hover:border-gray-${cancelButtonHoverValue} py-2 px-8 text-sm rounded w-1/2`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            className={`py-2 px-8 text-sm rounded w-1/2 bg-[#673ab7] hover:bg-[#5121A3] text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? loadingButtonTitle : 'Confirm'}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AppDialog;
